import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CreditCard, Upload, Mail, Phone, MapPin, ShoppingBag, Truck, CheckCircle } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useNavigate } from 'react-router-dom';
import { calculateShippingCost, formatShippingCost, getShippingCategoryInfo } from '../utils/shipping';
import { customerStorage, draftOrderStorage, orderHistoryStorage } from '../utils/storage';
import type { ShippingCategory } from '../admin/types';
import toast from 'react-hot-toast';
import { trackConversion, trackFunnelStep } from '../utils/analytics';

interface CustomerDetails {
  name: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  postalCode: string;
}

interface CheckoutFormData extends CustomerDetails {
  specialInstructions: string;
  deliveryInstructions: string;
  deliveryMethod: 'delivery' | 'collection';
}

interface ShippingInfo {
  category: ShippingCategory;
  cost: number;
  description: string;
}

const Checkout: React.FC = () => {
  const { state, clearCart } = useCart();
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [proofOfPayment, setProofOfPayment] = useState<File | null>(null);
  const [shippingInfo, setShippingInfo] = useState<ShippingInfo | null>(null);
  const [formData, setFormData] = useState<CheckoutFormData>(() => {
    // Load saved customer details on component mount
    const savedDetails = customerStorage.getFormDefaults();
    return {
      name: savedDetails.name || '',
      email: savedDetails.email || '',
      phone: savedDetails.phone || '',
      address: savedDetails.address || '',
      city: savedDetails.city || '',
      postalCode: savedDetails.postalCode || '',
      specialInstructions: '',
      deliveryInstructions: savedDetails.deliveryInstructions || '',
      deliveryMethod: 'delivery' // Default to delivery
    };
  });

  const bankingDetails = {
    bank: 'Absa Bank',
    accountName: 'GJV EVERTS',
    accountNumber: '1004543595',
    branchCode: '632005',
    reference: `MCG${Date.now()}`,
    proofEmail: 'mike@silvertoncc.co.za'
  };

  // Load draft order and customer details on mount
  useEffect(() => {
    const loadSavedData = () => {
      // Load draft order if exists
      const draft = draftOrderStorage.load();
      if (draft?.customer) {
        const savedCustomer = draft.customer;
        setFormData(prev => ({
          ...prev,
          ...savedCustomer
        }));
        
        // Show notification about restored data
        toast.success('Previous checkout session restored', { duration: 4000 });
      }
    };

    loadSavedData();
  }, []);

  // Calculate shipping on component mount and when cart changes or delivery method changes
  useEffect(() => {
    const loadShipping = async () => {
      try {
        if (formData.deliveryMethod === 'collection') {
          // Free shipping for collection
          setShippingInfo({
            category: 'small', // Doesn't matter for collection
            cost: 0,
            description: 'In-store collection - Free'
          });
        } else {
          // Calculate shipping for delivery
          const shipping = await calculateShippingCost(state.items);
          setShippingInfo(shipping);
        }
      } catch (error) {
        console.error('Error calculating shipping:', error);
        toast.error('Failed to calculate shipping cost');
      }
    };

    if (state.items.length > 0) {
      loadShipping();
    }
  }, [state.items, formData.deliveryMethod]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      
      // Save customer details to localStorage (excluding special instructions)
      if (name !== 'specialInstructions') {
        customerStorage.save({ [name]: value });
      }
      
      return updated;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/') && file.type !== 'application/pdf') {
        toast.error('Please upload an image or PDF file');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      
      setProofOfPayment(file);
      toast.success('Proof of payment attached');
    }
  };

  const uploadProofOfPayment = async (file: File, orderId: string): Promise<string | null> => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${orderId}_${Date.now()}.${fileExt}`;
      const filePath = `proof-of-payments/${fileName}`;

      const { supabase } = await import('../admin/lib/supabaseClient');
      if (!supabase || !supabase.storage) {
        console.warn('Storage not available in this environment');
        return null;
      }

      const { error: uploadError } = await supabase.storage
        .from('orders')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      const { data } = supabase.storage
        .from('orders')
        .getPublicUrl(filePath);

      return data.publicUrl;
    } catch (error) {
      console.error('Error uploading proof of payment:', error);
      toast.error('Failed to upload proof of payment');
      return null;
    }
  };

  const submitOrder = async () => {
    try {
      setLoading(true);
      
      // Track order submission attempt
      trackFunnelStep('checkout', 'order_submission_started', { 
        itemCount: state.items.length,
        cartTotal: state.totalAmount 
      });

      if (!shippingInfo) {
        toast.error('Unable to calculate shipping cost');
        return;
      }

      const totalWithShipping = state.totalAmount + (shippingInfo.cost / 100); // Convert cents to rands

      // Create order in database
      const orderData = {
        customer_name: formData.name,
        customer_email: formData.email,
        customer_phone: formData.phone,
        customer_address: `${formData.address}, ${formData.city}, ${formData.postalCode}`,
        delivery_address: formData.deliveryMethod === 'delivery' 
          ? `${formData.address}, ${formData.city}, ${formData.postalCode}`
          : 'In-store collection - Silverton Workshop',
        delivery_method: formData.deliveryMethod,
        delivery_instructions: formData.deliveryInstructions || null,
        total_amount: Math.round(totalWithShipping * 100), // Convert to cents
        shipping_category: formData.deliveryMethod === 'delivery' ? shippingInfo.category : null,
        shipping_cost: shippingInfo.cost,
        status: 'pending',
        cart_items: state.items,
        special_instructions: formData.specialInstructions || null,
        banking_reference: bankingDetails.reference,
        proof_of_payment_url: null // Initialize as null
      };

      const { supabase } = await import('../admin/lib/supabaseClient');

      // Reserve stock for each cart item atomically via RPC. If any reservation fails, release previous reservations and abort.
      const { reserveStock, releaseStock } = await import('../utils/inventory');
      const reserved: Array<{ id: string; qty: number }> = [];

      for (const item of state.items) {
        const productId = item.id as string;
        const qty = item.quantity || 1;
        const ok = await reserveStock(supabase, productId, qty);
        if (!ok) {
          // release any previously reserved
          for (const r of reserved) {
            try {
              await releaseStock(supabase, r.id, r.qty);
            } catch (e) {
              // Release failed, but continue
            }
          }
          toast.error(`Failed to reserve stock for ${item.name || productId}. It may be out of stock.`);
          setLoading(false);
          return;
        }
        reserved.push({ id: productId, qty });
      }

      const { data: order, error: orderError } = await supabase
        .from('orders')
        .insert([orderData])
        .select()
        .single();

      if (orderError) {
        throw orderError;
      }

      // Upload proof of payment if provided
      let proofUrl = null;
      if (proofOfPayment) {
        proofUrl = await uploadProofOfPayment(proofOfPayment, order.id);
        
        if (proofUrl) {
          const { error: updateError } = await supabase
            .from('orders')
            .update({ proof_of_payment_url: proofUrl })
            .eq('id', order.id);

          if (updateError) {
            throw updateError;
          }
        }
      }
      
      // Track successful purchase conversion
      trackConversion('purchase', totalWithShipping, {
        orderId: order.id,
        reference: bankingDetails.reference,
        itemCount: state.items.length,
        shippingMethod: formData.deliveryMethod,
        hasProofOfPayment: !!proofUrl
      });
      trackFunnelStep('checkout', 'order_completed', { 
        orderId: order.id,
        totalAmount: totalWithShipping 
      });
      
      // Save to order history
      orderHistoryStorage.add({
        id: order.id,
        reference: bankingDetails.reference,
        email: formData.email,
        timestamp: Date.now()
      });
      
      // Clear draft order since it's now submitted
      draftOrderStorage.clear();
      
      toast.success('Order submitted successfully!');
      clearCart();
      navigate('/order-confirmation', { 
        state: { 
          orderId: order.id,
          reference: bankingDetails.reference,
          customerEmail: formData.email
        }
      });

    } catch (error) {
      trackFunnelStep('checkout', 'order_error', { 
        error: String(error),
        step: 'submission' 
      });
      toast.error('Failed to submit order. Please try again or contact support.');
    } finally {
      setLoading(false);
    }
  };

  // Save draft order when advancing steps or significant changes
  const saveDraftOrder = () => {
    if (formData.email && state.items.length > 0) {
      draftOrderStorage.save({
        customer: formData,
        cartItems: state.items,
        shippingInfo,
        totalAmount: state.totalAmount + (shippingInfo?.cost || 0) / 100,
        reference: bankingDetails.reference
      });
    }
  };

  const nextStep = () => {
    if (currentStep === 1) {
      // Validate customer details
      const requiredFields = ['name', 'email', 'phone', 'address', 'city', 'postalCode'];
      const missingFields = requiredFields.filter(field => !formData[field as keyof CheckoutFormData]);
      
      if (missingFields.length > 0) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      if (!formData.email.includes('@')) {
        toast.error('Please enter a valid email address');
        return;
      }
      
      // Save draft when moving to payment step
      saveDraftOrder();
      trackFunnelStep('checkout', 'shipping_details_completed', { 
        deliveryMethod: formData.deliveryMethod,
        shippingCost: shippingInfo?.cost 
      });
    }
    
    if (currentStep === 2) {
      // Save draft when moving to confirmation step
      saveDraftOrder();
      trackFunnelStep('checkout', 'payment_details_added', { 
        hasProofOfPayment: !!proofOfPayment 
      });
    }
    
    setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    setCurrentStep(currentStep - 1);
  };

  // Redirect if cart is empty
  if (state.items.length === 0) {
    return (
  <div className="min-h-[80vh] md:min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <ShoppingBag size={64} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
            Your cart is empty
          </h2>
          <button
            onClick={() => navigate('/shop')}
            className="px-6 py-3 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
          >
            Continue Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
  <div className="min-h-[80vh] md:min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <div className="bg-white dark:bg-gray-900 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/shop')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              Checkout
            </h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex items-center">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                  step <= currentStep 
                    ? 'bg-mini-red text-white' 
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
                }`}>
                  {step}
                </div>
                {step < 3 && (
                  <div className={`flex-1 h-1 mx-4 ${
                    step < currentStep 
                      ? 'bg-mini-red' 
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">Details</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">Payment</span>
            <span className="text-sm text-gray-600 dark:text-gray-400">Confirm</span>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <motion.div
              key={currentStep}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6"
            >
              {currentStep === 1 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Customer Details
                  </h2>
                  
                  {(formData.name || formData.email) && (
                    <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <p className="text-sm text-green-700 dark:text-green-300">
                        ✓ Your details are automatically saved as you type for convenience
                      </p>
                    </div>
                  )}
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your full name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your email"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your phone number"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Postal Code *
                      </label>
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter postal code"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Street Address *
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your street address"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        City *
                      </label>
                      <input
                        type="text"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Enter your city"
                        required
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                        Delivery Method *
                      </label>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.deliveryMethod === 'delivery'
                            ? 'border-mini-red bg-mini-red/5 dark:bg-mini-red/10'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}>
                          <input
                            type="radio"
                            name="deliveryMethod"
                            value="delivery"
                            checked={formData.deliveryMethod === 'delivery'}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              formData.deliveryMethod === 'delivery'
                                ? 'border-mini-red bg-mini-red'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}>
                              {formData.deliveryMethod === 'delivery' && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <Truck size={20} className="text-gray-600 dark:text-gray-400" />
                                <span className="font-medium text-gray-900 dark:text-white">PUDO Delivery</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Delivered to your nearest PUDO collection point
                              </p>
                              {shippingInfo && formData.deliveryMethod === 'delivery' && (
                                <p className="text-sm font-medium text-mini-red mt-1">
                                  {formatShippingCost(shippingInfo.cost)}
                                </p>
                              )}
                            </div>
                          </div>
                        </label>

                        <label className={`relative flex items-center p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          formData.deliveryMethod === 'collection'
                            ? 'border-mini-red bg-mini-red/5 dark:bg-mini-red/10'
                            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
                        }`}>
                          <input
                            type="radio"
                            name="deliveryMethod"
                            value="collection"
                            checked={formData.deliveryMethod === 'collection'}
                            onChange={handleInputChange}
                            className="sr-only"
                          />
                          <div className="flex items-center gap-3">
                            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                              formData.deliveryMethod === 'collection'
                                ? 'border-mini-red bg-mini-red'
                                : 'border-gray-300 dark:border-gray-600'
                            }`}>
                              {formData.deliveryMethod === 'collection' && (
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <MapPin size={20} className="text-gray-600 dark:text-gray-400" />
                                <span className="font-medium text-gray-900 dark:text-white">Store Collection</span>
                              </div>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                Collect from our workshop in Silverton
                              </p>
                              <p className="text-sm font-medium text-green-600 dark:text-green-400 mt-1">
                                Free
                              </p>
                            </div>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        {formData.deliveryMethod === 'delivery' ? 'Delivery Instructions (Optional)' : 'Collection Notes (Optional)'}
                      </label>
                      <textarea
                        name="deliveryInstructions"
                        value={formData.deliveryInstructions}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder={
                          formData.deliveryMethod === 'delivery'
                            ? "Any special delivery instructions..."
                            : "Any notes for collection (preferred time, contact person, etc.)..."
                        }
                      />
                      {formData.deliveryMethod === 'delivery' && (
                        <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                          <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 mb-1">
                            <Truck size={16} />
                            <span className="font-medium text-sm">PUDO Delivery Information</span>
                          </div>
                          <p className="text-xs text-blue-700 dark:text-blue-300">
                            All orders are delivered via PUDO courier services. You can collect your order from your nearest PUDO box or kiosk. 
                            Tracking and confirmation details will be emailed to you once your order is dispatched.
                          </p>
                        </div>
                      )}
                      {formData.deliveryMethod === 'collection' && (
                        <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                          <div className="flex items-center gap-2 text-green-800 dark:text-green-200 mb-1">
                            <MapPin size={16} />
                            <span className="font-medium text-sm">Collection Information</span>
                          </div>
                          <p className="text-xs text-green-700 dark:text-green-300">
                            Collect your order from our workshop in Silverton. We'll contact you when your order is ready for collection.
                            Workshop hours: Monday-Friday 8:00-17:00, Saturday 8:00-13:00.
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Special Instructions (Optional)
                      </label>
                      <textarea
                        name="specialInstructions"
                        value={formData.specialInstructions}
                        onChange={handleInputChange}
                        rows={3}
                        className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-mini-red focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Any special instructions for your order..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Payment Information
                  </h2>

                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 mb-2">
                      <CreditCard size={20} />
                      <span className="font-medium">EFT Payment Required</span>
                    </div>
                    <p className="text-blue-700 dark:text-blue-300 text-sm">
                      Please make an EFT payment using the banking details below, then upload your proof of payment.
                    </p>
                  </div>

                  <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Banking Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                        <span className="block text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wide">Bank:</span>
                        <span className="block font-medium text-gray-900 dark:text-white">{bankingDetails.bank}</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                        <span className="block text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wide">Account Name:</span>
                        <span className="block font-medium text-gray-900 dark:text-white">{bankingDetails.accountName}</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                        <span className="block text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wide">Account Number:</span>
                        <span className="block font-medium text-gray-900 dark:text-white">{bankingDetails.accountNumber}</span>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-lg">
                        <span className="block text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wide">Branch Code:</span>
                        <span className="block font-medium text-gray-900 dark:text-white">{bankingDetails.branchCode}</span>
                      </div>
                      <div className="md:col-span-2 bg-mini-red/10 dark:bg-mini-red/20 p-3 rounded-lg border border-mini-red/20">
                        <span className="block text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wide">Reference (Important):</span>
                        <span className="block font-bold text-mini-red text-lg">{bankingDetails.reference}</span>
                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-1">Please use this reference when making payment</span>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Upload Proof of Payment (Optional)
                    </label>
                    <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 text-center">
                      <Upload size={32} className="mx-auto text-gray-400 mb-2" />
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                        Upload your payment slip or screenshot
                      </p>
                      <input
                        type="file"
                        onChange={handleFileChange}
                        accept="image/*,.pdf"
                        className="hidden"
                        id="proof-upload"
                      />
                      <label
                        htmlFor="proof-upload"
                        className="cursor-pointer inline-flex items-center px-4 py-2 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
                      >
                        Choose File
                      </label>
                      {proofOfPayment && (
                        <p className="text-sm text-green-600 dark:text-green-400 mt-2">
                          ✓ {proofOfPayment.name} attached
                        </p>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                      Accepted formats: JPG, PNG, PDF (max 5MB)
                    </p>
                    <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-2 text-green-800 dark:text-green-200 mb-1">
                        <Mail size={16} />
                        <span className="font-medium text-sm">Alternative: Email Proof of Payment</span>
                      </div>
                      <p className="text-xs text-green-700 dark:text-green-300">
                        You can also email your proof of payment directly to: 
                        <a href={`mailto:${bankingDetails.proofEmail}`} className="font-medium underline ml-1">
                          {bankingDetails.proofEmail}
                        </a>
                      </p>
                      <p className="text-xs text-green-600 dark:text-green-400 mt-1">
                        Please include your order reference: <span className="font-mono font-bold">{bankingDetails.reference}</span>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 3 && (
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                    Order Confirmation
                  </h2>

                  <div className="space-y-6">
                    {/* Customer Details */}
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-3">Customer Details</h3>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm">
                        <p><strong>Name:</strong> {formData.name}</p>
                        <p><strong>Email:</strong> {formData.email}</p>
                        <p><strong>Phone:</strong> {formData.phone}</p>
                        <p><strong>Address:</strong> {formData.address}, {formData.city}, {formData.postalCode}</p>
                        {formData.deliveryInstructions && (
                          <p><strong>Delivery Instructions:</strong> {formData.deliveryInstructions}</p>
                        )}
                      </div>
                    </div>

                    {/* Delivery Method Information */}
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-3">Delivery Method</h3>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm">
                        <div className="flex items-center gap-2 mb-2">
                          {formData.deliveryMethod === 'delivery' ? (
                            <Truck size={16} className="text-gray-400" />
                          ) : (
                            <MapPin size={16} className="text-gray-400" />
                          )}
                          <span><strong>Method:</strong> {formData.deliveryMethod === 'delivery' ? 'PUDO Delivery' : 'Store Collection'}</span>
                        </div>
                        {formData.deliveryMethod === 'delivery' ? (
                          <div>
                            <p><strong>Address:</strong> {formData.address}, {formData.city}, {formData.postalCode}</p>
                            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                              Delivered to your nearest PUDO collection point
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p><strong>Location:</strong> Silverton Workshop</p>
                            <p className="text-gray-600 dark:text-gray-400 text-xs mt-1">
                              Monday-Friday 8:00-17:00, Saturday 8:00-13:00
                            </p>
                          </div>
                        )}
                        {formData.deliveryInstructions && (
                          <p className="mt-2"><strong>Instructions:</strong> {formData.deliveryInstructions}</p>
                        )}
                      </div>
                    </div>

                    {/* Shipping Information */}
                    {shippingInfo && (
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">Cost Breakdown</h3>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm">
                          <div className="flex items-center gap-2 mb-2">
                            {formData.deliveryMethod === 'delivery' ? (
                              <Truck size={16} className="text-gray-400" />
                            ) : (
                              <MapPin size={16} className="text-gray-400" />
                            )}
                            <span><strong>Cost:</strong> {formatShippingCost(shippingInfo.cost)}</span>
                          </div>
                          {formData.deliveryMethod === 'delivery' ? (
                            <p className="text-gray-600 dark:text-gray-400 text-xs">
                              {getShippingCategoryInfo(shippingInfo.category).description}
                            </p>
                          ) : (
                            <p className="text-gray-600 dark:text-gray-400 text-xs">
                              No shipping charges for in-store collection
                            </p>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Payment Status */}
                    <div>
                      <h3 className="font-medium text-gray-900 dark:text-white mb-3">Payment Status</h3>
                      <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm">
                        <p><strong>Reference:</strong> {bankingDetails.reference}</p>
                        <p><strong>Status:</strong> {proofOfPayment ? 'Proof of payment attached' : 'Awaiting payment'}</p>
                        {proofOfPayment && (
                          <p className="text-green-600 dark:text-green-400">✓ {proofOfPayment.name}</p>
                        )}
                      </div>
                    </div>

                    {formData.specialInstructions && (
                      <div>
                        <h3 className="font-medium text-gray-900 dark:text-white mb-3">Special Instructions</h3>
                        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 text-sm">
                          <p>{formData.specialInstructions}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Navigation Buttons */}
              <div className="flex justify-between mt-8">
                <button
                  onClick={prevStep}
                  disabled={currentStep === 1}
                  className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>

                {currentStep < 3 ? (
                  <button
                    onClick={nextStep}
                    className="px-6 py-3 bg-mini-red text-white rounded-lg hover:bg-mini-red/90 transition-colors"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={submitOrder}
                    disabled={loading}
                    className="relative px-8 py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-all duration-200 disabled:cursor-not-allowed focus:ring-4 focus:ring-green-200 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:transform-none disabled:shadow-none"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        Submitting Order...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <CheckCircle size={18} />
                        Submit Order
                      </div>
                    )}
                  </button>
                )}
              </div>
            </motion.div>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 sticky top-8">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                Order Summary
              </h3>

              <div className="space-y-3 mb-4">
                {state.items.map((item) => (
                  <div key={item.id} className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-lg flex-shrink-0 overflow-hidden">
                      {item.image_url ? (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ShoppingBag size={16} className="text-gray-400" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                        {item.name}
                      </p>
                      <p className="text-xs text-gray-500">
                        {item.quantity} × R{item.price.toFixed(2)}
                      </p>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                      R{(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                  <span className="text-gray-900 dark:text-white">R{state.totalAmount.toFixed(2)}</span>
                </div>
                
                {shippingInfo && (
                  <div className="flex justify-between items-center">
                    <div className="flex items-center gap-2">
                      {formData.deliveryMethod === 'delivery' ? (
                        <Truck size={16} className="text-gray-400" />
                      ) : (
                        <MapPin size={16} className="text-gray-400" />
                      )}
                      <span className="text-gray-600 dark:text-gray-400">
                        {formData.deliveryMethod === 'delivery' 
                          ? `Shipping (${getShippingCategoryInfo(shippingInfo.category).name}):`
                          : 'Collection:'
                        }
                      </span>
                    </div>
                    <span className="text-gray-900 dark:text-white">
                      {formatShippingCost(shippingInfo.cost)}
                    </span>
                  </div>
                )}
                
                <div className="border-t border-gray-200 dark:border-gray-700 pt-2">
                  <div className="flex justify-between items-center text-lg font-semibold">
                    <span className="text-gray-900 dark:text-white">Total:</span>
                    <span className="text-mini-red">
                      R{(state.totalAmount + (shippingInfo?.cost || 0) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                {shippingInfo && (
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    <div className="flex items-center gap-1">
                      {formData.deliveryMethod === 'delivery' ? (
                        <>
                          {getShippingCategoryInfo(shippingInfo.category).icon}
                          <span>{getShippingCategoryInfo(shippingInfo.category).description}</span>
                        </>
                      ) : (
                        <>
                          <MapPin size={14} />
                          <span>Free collection from Silverton workshop</span>
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;